import clsx from "clsx";
import { X } from "lucide-react";
import { type ReactNode, useId, useRef } from "react";

import { motion, useClosing } from "./presence";
import { BetaBadge, IconButton, type IconProp, renderIcon } from "./ui";
import { useDismiss } from "./useDismiss";
import { useFocusTrap } from "./useFocusTrap";
import { useMediaQuery } from "./useMediaQuery";
import { t } from "../i18n/i18n";

/** Volet latéral d'une vue plein cadre (R7, R16) : éditeur de l'OPO, détail d'un incident de la DIREX, wargame.
 *  À partir de `overlayBelow` (1280 px), il s'ouvre en largeur à droite du contenu (`Drawer`) ; en dessous
 *  (tablette), il passe en surimpression sur le bord droit, sous un voile `bg-ink/20` qui le ferme au toucher, et
 *  Échap le ferme (pile partagée). À poser dans le conteneur `relative` de la vue, entouré de `Presence` pour
 *  jouer son entrée et sa sortie.
 *
 *  - En-tête de 36 px : icône de 16 px, titre en 12 px semi-gras, `actions` (`IconButton size="sm"`, « ? » en
 *    dernier), puis la croix.
 *  - `tabs` : rangée sous l'en-tête (`<Tabs>`), retour à la ligne au besoin.
 *  - `footer` : barre d'action collante sous le corps qui défile.
 *
 *  Le passage d'un mode à l'autre (rotation d'un iPad Pro, fenêtre redimensionnée) ne remonte pas le contenu : le
 *  volet garde la même place dans l'arbre React (seules ses classes changent, le voile est un frère rendu APRÈS
 *  lui), si bien que la saisie en cours, le défilement et l'onglet survivent, et l'entrée n'est pas rejouée. En
 *  surimpression, le focus entre dans le volet et y reste (`useFocusTrap`), puis revient où il était. */
export function SidePanel({
  title,
  icon,
  actions,
  tabs,
  footer,
  onClose,
  width = 440,
  overlayBelow = 1280,
  children,
  className,
}: {
  title: ReactNode;
  icon?: IconProp;
  actions?: ReactNode;
  tabs?: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  width?: number;
  /** Largeur de fenêtre sous laquelle le volet passe en surimpression. */
  overlayBelow?: number;
  children: ReactNode;
  className?: string;
}) {
  const wide = useMediaQuery(`(min-width: ${overlayBelow}px)`);
  const closing = useClosing();
  const titleId = useId();
  const panel = useRef<HTMLElement>(null);
  // Échap ne ferme le volet qu'en surimpression : en ligne, il fait partie de la vue.
  useDismiss(!wide && !closing, onClose);
  useFocusTrap(panel, !wide && !closing);
  // Entrée jouée dans le mode d'ouverture seulement : après un changement de mode, pas de nouvelle entrée.
  const openedWide = useRef(wide);
  const switched = useRef(false);
  if (wide !== openedWide.current) switched.current = true;
  const enter = !switched.current;

  return (
    <>
      <aside
        ref={panel}
        role="complementary"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={clsx(
          "flex overflow-hidden border-l border-line bg-surface outline-none",
          wide
            ? clsx("shrink-0", closing ? "gx-drawer-out" : enter && "gx-drawer-in")
            : clsx("absolute inset-y-0 right-0 z-40 max-w-[92%] shadow-float", (closing || enter) && motion("right", closing)),
          className,
        )}
        style={{ width }}
      >
        <div className="flex h-full min-h-0 w-full shrink-0 flex-col" style={wide ? { width } : undefined}>
          <header className="flex h-9 shrink-0 items-center gap-2 border-b border-line pr-1.5 pl-4">
            {icon !== undefined && <span className="flex shrink-0 text-ink-muted">{renderIcon(icon, 16)}</span>}
            <h2 id={titleId} className="min-w-0 flex-1 truncate text-ui font-semibold text-ink">
              {title}
            </h2>
            <BetaBadge />
            <div className="flex shrink-0 items-center gap-0.5">
              {actions}
              <IconButton size="sm" onClick={onClose} title={t("Fermer")} aria-label={t("Fermer")}>
                <X size={16} />
              </IconButton>
            </div>
          </header>
          {tabs && <div className="shrink-0 border-b border-line px-3 py-1.5">{tabs}</div>}
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
          {footer && <div className="flex shrink-0 items-center gap-2 border-t border-line bg-surface px-4 py-3">{footer}</div>}
        </div>
      </aside>
      {/* Voile de la surimpression, sous le volet (z-30 < z-40), rendu après lui pour ne pas déplacer le volet. */}
      {!wide && (
        <div
          className={clsx("absolute inset-0 z-30 bg-ink/20", motion("fade", closing))}
          onClick={closing ? undefined : onClose}
          aria-hidden
        />
      )}
    </>
  );
}
